import { useGoogleLogin } from "@react-oauth/google";
import { Link, useNavigate } from "react-router";
import { LoginButton } from "@telegram-auth/react";
import { useGlobals } from "../../hooks/useGlobals";
import MemberService from "../../services/MemberService";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import LegalModal from "../../components/LegalModal";

function LoginPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { setAuthMember } = useGlobals();
  const [error, setError] = useState("");
  const [showTerms, setShowTerms] = useState(false);
  const [showPrivacy, setShowPrivacy] = useState(false);

  const urlParams = new URLSearchParams(window.location.search);

  const googleAuth = useGoogleLogin({
    onSuccess: async (codeResponse) => {
      try {
        setError("");
        const memberService = new MemberService();

        const { accessToken, refreshToken, member } =
          await memberService.googleAuth(codeResponse);

        localStorage.setItem("accessToken", accessToken);
        localStorage.setItem("refreshToken", refreshToken);

        setAuthMember(member);

        navigate("/");
      } catch (err: any) {
        console.error("Google auth error:", err);
        setError(t("login.errors.googleFailed"));

        if (err?.response?.data?.message) {
          alert(err?.response?.data?.message);
        }
      }
    },
    onError: (error) => {
      console.error("Login Failed:", error);
      setError(t("login.errors.loginFailed"));
    },
    flow: "auth-code",
  });

  const githubAuth = () => {
    try {
      const githubUrl = `https://github.com/login/oauth/authorize?client_id=${process.env.REACT_APP_GITHUB_CLIENT_ID}&redirect_uri=${process.env.REACT_APP_GITHUB_REDIRECT_URL}&scope=user:email`;
      window.location.href = githubUrl;
    } catch (err) {
      console.error("GitHub auth error:", err);
      setError(t("login.errors.githubFailed"));
    }
  };

  const handleTelegramAuth = async (data: any) => {
    try {
      setError("");
      const memberService = new MemberService();
      const { accessToken, refreshToken, member } =
        await memberService.telegramAuth(data);
      localStorage.setItem("accessToken", accessToken);
      localStorage.setItem("refreshToken", refreshToken);
      setAuthMember(member);
      navigate("/");
    } catch (err: any) {
      console.error("Telegram auth error:", err);
      setError(t("login.errors.telegramFailed"));
      if (err?.response?.data?.message) {
        alert(err?.response?.data?.message);
      }
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-100 via-purple-50 to-pink-100 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Header */}
          <div className="text-center mb-8">
            <Link
              to="/"
              className="inline-flex items-center justify-center space-x-2 mb-6 hover:opacity-80 transition-opacity"
            >
              <img src="./logo-crop.png" alt="" />
            </Link>
            <h2 className="text-3xl font-bold text-gray-900 mb-2">
              {t("login.welcome")}
            </h2>
            <p className="text-gray-600">{t("login.subtitle")}</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg flex items-start gap-3">
              <span className="mt-0.5 text-lg">⚠️</span>
              <div className="flex-1">
                <p className="font-medium">{error}</p>
              </div>
              <button
                onClick={() => setError("")}
                className="ml-auto font-bold hover:text-red-900 transition-colors"
              >
                ✕
              </button>
            </div>
          )}

          {/* Social Login Buttons */}
          <div className="space-y-3 mb-6">
            {/* Google Button */}
            <button
              onClick={() => googleAuth()}
              className="w-full flex items-center justify-center space-x-3 bg-white border-2 border-gray-300 rounded-lg px-4 py-3 hover:bg-gray-50 hover:border-gray-400 transition-all font-medium hover:shadow-md transform hover:scale-[1.02]"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path
                  fill="#4285F4"
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                />
                <path
                  fill="#34A853"
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                />
                <path
                  fill="#FBBC05"
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                />
                <path
                  fill="#EA4335"
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                />
              </svg>
              <span>{t("login.googleButton")}</span>
            </button>

            {/* GitHub Button */}
            <button
              onClick={githubAuth}
              className="w-full flex items-center justify-center space-x-3 bg-gray-900 text-white rounded-lg px-4 py-3 hover:bg-gray-800 transition-all font-medium hover:shadow-md transform hover:scale-[1.02]"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
              </svg>
              <span>{t("login.githubButton")}</span>
            </button>

            {/* Telegram Button */}
            <div
              className="flex justify-center items-center rounded-lg overflow-hidden"
              style={{
                width: "100%",
                height: "48px",
                backgroundColor: "#54a9eb",
              }}
            >
              <LoginButton
                botUsername={String(
                  process.env.REACT_APP_TELEGRAM_BOT_USERNAME
                )}
                buttonSize="large"
                cornerRadius={8}
                showAvatar={true}
                lang="en"
                onAuthCallback={handleTelegramAuth}
              />
            </div>
          </div>

          {/* Terms */}
          <div className="mt-6 text-center text-xs text-gray-500">
            <p className="leading-relaxed">
              {t("login.terms")}{" "}
              <span
                onClick={() => setShowTerms(true)}
                className="text-indigo-600 hover:text-indigo-700 underline"
              >
                {t("login.termsLink")}
              </span>{" "}
              {t("login.and")}{" "}
              <span
                onClick={() => setShowPrivacy(true)}
                className="text-indigo-600 hover:text-indigo-700 underline"
              >
                {t("login.privacyLink")}
              </span>
            </p>
          </div>
        </div>
      </div>

      <LegalModal
        isOpen={showTerms}
        onClose={() => setShowTerms(false)}
        title="terms"
      />

      <LegalModal
        isOpen={showPrivacy}
        onClose={() => setShowPrivacy(false)}
        title="privacy"
      />
    </div>
  );
}

export default LoginPage;
